import ComposePlugins, { addComposePlugins } from './ComposePlugins';
import ContentOperationPlugin from './contentOperation/ContentOperationPlugin';
import DockingPlugin from './docking/DockingPlugin';
import StickyDockingPlugin from './docking/StickyDockingPlugin';
import preloadComposePlugins from '../actions/preloadComposePlugins';
import switchToReadWriteRecipient from '../actions/switchToReadWriteRecipient';
import ComposeViewState from '../store/schema/ComposeViewState';
import canModifyRecipients from '../utils/canModifyRecipients';
import createAttachments from '../utils/createAttachments';
import deletePendingInlineAttachment from '../utils/deletePendingInlineAttachment';
import fixInlineImageSrc from '../utils/fixInlineImageSrc';
import getCurrentFromAddress from '../utils/getCurrentFromAddress';
import isSmartComposeEnabled from '../utils/isSmartComposeEnabled';
import { AddinEditorPlugin } from 'owa-addins-editor-plugin';
import { AttachmentFile, AttachmentFileList } from 'owa-attachment-file-types';
import { CreateAttachmentsProperties, InlineAttachmentState } from 'owa-attachment-well-data';
import { LazyCortanaPickerPluginClass } from 'owa-cortana-compose';
import { UndoService } from 'owa-editor';
import { LazyAccCheckerPluginClass } from 'owa-editor-acc-checker-plugin';
import { LazyEmojiPickerPluginClass } from 'owa-editor-emoji-picker-plugin';
import { LazyEmojiPluginClass } from 'owa-editor-emojiplugin';
import { LazyLinkPluginClass } from 'owa-editor-link-plugin';
import { LazyAtMentionsPickerPluginClass } from 'owa-editor-mentions-picker-plugin';
import { LazyPasteOptionPluginClass } from 'owa-editor-paste-option-plugin';
import RibbonPlugin from 'owa-editor-ribbonplugin/lib/plugin/RibbonPlugin';
import { LazySmartSuggestionsPluginClass } from 'owa-editor-smart-suggestions-plugin';
import { LazySmartComposePluginClass } from 'owa-editor-smartcompose-plugin';
import { TableEditPlugin } from 'owa-editor-table-edit-plugin';
import { isFeatureEnabled } from 'owa-feature-flags';
import { LazyLinkPreviewPluginClass } from 'owa-link-preview-compose';
import { getRecipientWellsFromComposeViewState } from '../utils/getAllRecipientsAsEmailAddressStrings';
import getRecipientsFromRecipientWells from 'owa-recipient-common/lib/utils/getRecipientsFromRecipientWells';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import {
    InlineImagePlugin,
    LazyInlineImageResizePluginClass,
    CreateAttachments,
    DeletePendingInlineAttachment,
    lazyGetSelectedImage,
} from 'owa-inline-image';

function getCreateAttachmentsHandler(viewState: ComposeViewState): CreateAttachments {
    return function(
        files: AttachmentFileList,
        isInline: boolean,
        shouldShare: boolean,
        isHiddenInline: boolean,
        processInlineImageBeforeUploadFunc: (temporaryId: string, file: AttachmentFile) => void
    ) {
        const baseProperties = { isInline, isHiddenInline };
        const createAttachmentsProperties: CreateAttachmentsProperties = shouldShare
            ? { ...baseProperties, shouldShare: true }
            : { ...baseProperties, shouldShare: false };
        createAttachments(
            files,
            viewState,
            createAttachmentsProperties,
            processInlineImageBeforeUploadFunc
        );
    };
}

function getDeleteInlineAttachmentsHandler(
    viewState: ComposeViewState
): DeletePendingInlineAttachment {
    return function(placeholderId: string) {
        deletePendingInlineAttachment(placeholderId, viewState);
    };
}

function getAttachmentStatesHandler(viewState: ComposeViewState): () => InlineAttachmentState[] {
    return () => viewState.attachmentWell.inlineAttachments;
}

function getSubjectHandler(viewState: ComposeViewState): () => string {
    return () => viewState.subject;
}

export default function createComposePlugins(
    viewState: ComposeViewState,
    undo: UndoService
): ComposePlugins {
    const dockingPlugin = viewState.docking.useStickyDocking
        ? new StickyDockingPlugin(viewState.docking)
        : new DockingPlugin(viewState.docking);
    const scrollAdapter = dockingPlugin.getScrollAdapter();

    // PasteManager depends on InlineImagePlugin to attach inline images.
    const inlineImagePlugin = new InlineImagePlugin(
        getAttachmentStatesHandler(viewState),
        getCreateAttachmentsHandler(viewState),
        getDeleteInlineAttachmentsHandler(viewState),
        fixInlineImageSrc(viewState)
    );

    let emojiPlugin = null;
    let emojiPickerPlugin = null;
    if (isFeatureEnabled('cmp-newEmoji-plugin')) {
        emojiPickerPlugin = new LazyEmojiPickerPluginClass(
            viewState.emojiPicker,
            scrollAdapter,
            true /* useFlexPane */,
            {
                elementIdPrefix: 'EMOJI',
                changeSource: 'EmojiPicker',
                triggerCharacter: ':',
                isHorizontal: true,
                handleAutoComplete: true,
            }
        );
    } else {
        emojiPlugin = new LazyEmojiPluginClass(
            viewState.emoji,
            scrollAdapter,
            true /*useFlexPane*/
        );
    }

    const getAllRecipientsFunc = (viewState: ComposeViewState) => () => {
        return getRecipientsFromRecipientWells(getRecipientWellsFromComposeViewState(viewState));
    };

    const getAllRecipientWellsFunc = (viewState: ComposeViewState) => () => {
        return getRecipientWellsFromComposeViewState(viewState);
    };

    const switchToReadWriteRecipientWell = (viewState: ComposeViewState) => () => {
        if (viewState.showCompactRecipientWell) {
            switchToReadWriteRecipient(viewState);
        }
    };

    let mentionsPickerPlugin = new LazyAtMentionsPickerPluginClass(
        viewState.atMentions,
        scrollAdapter,
        viewState.toRecipientWell,
        switchToReadWriteRecipientWell(viewState),
        canModifyRecipients(viewState),
        getAllRecipientsFunc(viewState),
        {
            elementIdPrefix: 'OWAAM',
            changeSource: 'AtMention',
            triggerCharacter: '@',
        }
    );

    const isRtl = getUserConfiguration().SessionSettings.IsUserCultureRightToLeft;
    const composePlugins: ComposePlugins = {
        undo: undo,
        dockingPlugin: dockingPlugin,
        ribbonPlugin: new RibbonPlugin(viewState.ribbon, lazyGetSelectedImage.import()),
        inlineImagePlugin: inlineImagePlugin,
        inlineImageResizePlugin: new LazyInlineImageResizePluginClass(scrollAdapter),
        emojiPlugin: emojiPlugin,
        mentionsPickerPlugin: mentionsPickerPlugin,
        cortanaComposePlugin:
            isFeatureEnabled('mc-cortanaCompose') &&
            new LazyCortanaPickerPluginClass(scrollAdapter),
        emojiPickerPlugin: emojiPickerPlugin,
        contentOperationPlugin: new ContentOperationPlugin(viewState),
        smartSuggestionsPlugin: isFeatureEnabled('mc-smartSuggestions')
            ? new LazySmartSuggestionsPluginClass()
            : null,
        linkPreviewPlugin: new LazyLinkPreviewPluginClass(scrollAdapter),
        addinPlugin: isFeatureEnabled('rp-addins') ? new AddinEditorPlugin() : null,
        pasteOptionPlugin: new LazyPasteOptionPluginClass(
            viewState.pasteOption,
            scrollAdapter,
            isRtl
        ),
        tableEdit: new TableEditPlugin(viewState.tableEdit),
        accCheckerPlugin: isFeatureEnabled('cmp-accChecker')
            ? new LazyAccCheckerPluginClass(viewState.accChecker)
            : null,
        linkPlugin: isFeatureEnabled('doc-attachment-sharingTips')
            ? new LazyLinkPluginClass(
                  viewState.attachmentWell,
                  scrollAdapter,
                  getRecipientWellsFromComposeViewState(viewState),
                  getCurrentFromAddress(viewState.toRecipientWell)
              )
            : null,
        smartComposePlugin: isSmartComposeEnabled()
            ? new LazySmartComposePluginClass(
                  getAllRecipientWellsFunc(viewState),
                  getSubjectHandler(viewState),
                  viewState.operation
              )
            : null,
    };

    // Setting the current compose plugins to the newly created one
    addComposePlugins(viewState, composePlugins);
    preloadComposePlugins(viewState);
    return composePlugins;
}
